import { Flex } from 'foundations-components/components/Flex';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { useState, useCallback, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useReactNodeView } from '../utils/ReactNodeView';
import styled, { css } from 'styled-components';
import { getTokenTooltip, PERSONALIZATION_TOKENS_P2 } from '../utils/personalizationTokenUtils';
import { PersonalizationTokenPopover } from '../commands/InsertPersonalizationToken';
import { CompaniesIcon } from 'foundations-assets/icons/components/CompaniesIcon';
import { ContactsIcon } from 'foundations-assets/icons/components/ContactsIcon';
import { MeetingsIcon } from 'foundations-assets/icons/components/MeetingsIcon';
import { UserIcon } from 'foundations-assets/icons/components/UserIcon';
import { WarningIcon } from 'foundations-assets/icons/components/WarningIcon';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const personalizationTokenIconMap = {
  companies: CompaniesIcon,
  contacts: ContactsIcon,
  meetings: MeetingsIcon,
  user: UserIcon
};
const stateStyles = {
  idle: css(["background-color:", ";border-color:", ";color:", ";cursor:pointer;&:hover{border-color:", ";}&:active{border-color:", ";}&:focus-visible{border-color:", ";outline:none;}"], ({
    theme
  }) => theme.color['fill-surface-default'], ({
    theme
  }) => theme.color['border-core-default'], ({
    theme
  }) => theme.color['text-core-default'], ({
    theme
  }) => theme.color['border-core-hover'], ({
    theme
  }) => theme.color['border-core-pressed'], ({
    theme
  }) => theme.color['border-interactive-default']),
  disabled: css(["background-color:", ";border-color:", ";color:", ";cursor:default;"], ({
    theme
  }) => theme.color['fill-surface-default'], ({
    theme
  }) => theme.color['border-core-disabled'], ({
    theme
  }) => theme.color['text-core-disabled']),
  error: css(["background-color:", ";border-color:", ";color:", ";cursor:pointer;&:hover{border-color:", ";}&:focus-visible{border-color:", ";outline:none;}"], ({
    theme
  }) => theme.color['fill-surface-default'], ({
    theme
  }) => theme.color['border-alert-default'], ({
    theme
  }) => theme.color['text-core-default'], ({
    theme
  }) => theme.color['border-alert-default'], ({
    theme
  }) => theme.color['border-interactive-default'])
};
const stateStylesP2 = {
  idle: css(["background-color:", ";border-color:", ";color:", ";cursor:pointer;&:hover{border-color:", ";}&:active{border-color:", ";}&:focus-visible{border-color:", ";outline:none;}"], ({
    theme
  }) => theme.color['fill-info-subtle'], ({
    theme
  }) => theme.color['border-core-default'], ({
    theme
  }) => theme.color['fill-info-default'], ({
    theme
  }) => theme.color['border-core-hover'], ({
    theme
  }) => theme.color['border-core-pressed'], ({
    theme
  }) => theme.color['border-interactive-default']),
  disabled: css(["background-color:", ";border-color:", ";color:", ";cursor:default;"], ({
    theme
  }) => theme.color['fill-info-subtle'], ({
    theme
  }) => theme.color['border-core-disabled'], ({
    theme
  }) => theme.color['text-core-disabled']),
  error: css(["background-color:", ";border-color:", ";color:", ";cursor:pointer;&:hover{border-color:", ";}&:focus-visible{border-color:", ";outline:none;}"], ({
    theme
  }) => theme.color['fill-surface-default'], ({
    theme
  }) => theme.color['border-alert-default'], ({
    theme
  }) => theme.color['text-core-default'], ({
    theme
  }) => theme.color['border-alert-default'], ({
    theme
  }) => theme.color['border-interactive-default'])
};
export const StyledTokenV2 = styled.span.withConfig({
  displayName: "PersonalizationTokenViewV2__StyledTokenV2"
})(["align-items:center;border:", " solid;border-radius:", ";display:inline-flex;font:inherit;padding:", ";vertical-align:baseline;user-select:text;white-space:nowrap;inline-size:auto;gap:", ";", ""], ({
  theme
}) => theme.borderWidth['100'], ({
  theme,
  $isP2
}) => $isP2 ? theme.borderRadius['100'] : '999px', ({
  theme
}) => `${theme.space['100']} ${theme.space['200']}`, ({
  theme
}) => theme.space['200'], ({
  tokenState,
  $isP2
}) => $isP2 ? stateStylesP2[tokenState] : stateStyles[tokenState]);
function DisplayText({
  displayText,
  tokenKey,
  defaultTokenValue
}) {
  const baseText = displayText || tokenKey;
  const text = defaultTokenValue ? `${baseText} (${defaultTokenValue})` : baseText;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: text
  });
}
export function PersonalizationTokenViewV2() {
  var _node$attrs;
  const {
    node,
    editorConfig,
    view,
    getPos
  } = useReactNodeView();
  const [open, setOpen] = useState(false);
  const auth = useContext(AuthContext);
  const isUngatedForP2 = Boolean(auth && auth.gates.includes(PERSONALIZATION_TOKENS_P2));
  const {
    tokenKey,
    defaultTokenValue,
    tokenStyles,
    tokenMeta,
    error,
    errorMessage,
    displayText
  } = (_node$attrs = node === null || node === void 0 ? void 0 : node.attrs) !== null && _node$attrs !== void 0 ? _node$attrs : {};
  const tokenIconKey = tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.tokenIconKey;
  const tooltipConfig = getTokenTooltip(editorConfig, tokenKey);
  const isDisabled = !(view !== null && view !== void 0 && view.editable);
  const isError = Boolean(error);
  const tokenState = isDisabled ? 'disabled' : isError ? 'error' : 'idle';
  const onOpenChange = useCallback(e => {
    if (e.target) {
      setOpen(e.target.value);
    }
  }, [setOpen]);
  const handleClick = useCallback(() => {
    if (isDisabled) return;
    setOpen(prevState => !prevState);
  }, [isDisabled]);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  const nodePos = typeof getPos === 'function' ? getPos() : undefined;
  const personalizationTokenPopover = () => /*#__PURE__*/_jsx(PersonalizationTokenPopover, {
    initialTokenKey: tokenKey,
    initialDefaultValue: defaultTokenValue,
    initialTokenMeta: tokenMeta,
    isUngatedForP2: isUngatedForP2,
    nodePos: nodePos,
    onPopoverConfirm: handleClose
  });
  const LeadingIcon = personalizationTokenIconMap[tokenIconKey];
  const TooltipIcon = tooltipConfig === null || tooltipConfig === void 0 ? void 0 : tooltipConfig.icon;
  const tokenContent =
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsxs(StyledTokenV2, {
    tokenState: tokenState,
    $isP2: isUngatedForP2,
    "data-test-id": "ProseMirror__personalizationTokenV2",
    onClick: handleClick,
    tabIndex: isDisabled ? undefined : 0,
    children: [LeadingIcon && /*#__PURE__*/_jsx(IconHolder, {
      icon: LeadingIcon,
      padding: "0"
    }), TooltipIcon && !LeadingIcon && /*#__PURE__*/_jsx(IconHolder, {
      icon: TooltipIcon,
      padding: "0"
    }), /*#__PURE__*/_jsx(DisplayText, {
      displayText: displayText,
      tokenKey: tokenKey,
      defaultTokenValue: defaultTokenValue
    }), isError && errorMessage && /*#__PURE__*/_jsx(Tooltip, {
      title: errorMessage,
      placement: "top",
      children: /*#__PURE__*/_jsx(Flex, {
        as: "span",
        alignItems: "center",
        "data-test-id": "ProseMirror__personalizationTokenV2__errorIcon",
        children: /*#__PURE__*/_jsx(IconHolder, {
          icon: WarningIcon,
          padding: "0"
        })
      })
    }), tooltipConfig && !isError && /*#__PURE__*/_jsx(HelpIcon, {
      title: tooltipConfig.title,
      tooltipPlacement: "top",
      size: "small",
      alignWithText: true
    })]
  });
  if (isDisabled) {
    return /*#__PURE__*/_jsx("span", {
      "data-test-id": "ProseMirror__personalizationTokenV2__wrapper",
      children: tokenContent
    });
  }
  return /*#__PURE__*/_jsx(Popover, {
    open: open,
    onOpenChange: onOpenChange,
    Content: personalizationTokenPopover,
    children: /*#__PURE__*/_jsx("span", {
      "data-test-id": "ProseMirror__personalizationTokenV2__wrapper",
      children: tokenContent
    })
  });
}
PersonalizationTokenViewV2.displayName = 'PersonalizationTokenViewV2';
export function personalizationTokenViewV2UpdateFn({
  previousNode,
  node
}) {
  if (previousNode.attrs.tokenKey !== node.attrs.tokenKey || previousNode.attrs.defaultTokenValue !== node.attrs.defaultTokenValue || previousNode.attrs.displayText !== node.attrs.displayText || previousNode.attrs.error !== node.attrs.error || previousNode.attrs.errorMessage !== node.attrs.errorMessage || JSON.stringify(previousNode.attrs.tokenMeta) !== JSON.stringify(node.attrs.tokenMeta)) {
    return false;
  }
  return true;
}